// What one SSE frame of `GET /runs/{run_id}/events/stream` carries (#158), and
// what the reducer folds (#159).
//
// The stream route is not in the OpenAPI document — `contracts/openapi.py`
// says the SPA's own routes arrive with the units that build them — so the
// frame's shape is written here, once, against `control_plane/sse.py`'s
// `frame_data`: the envelope's seven fields plus the server's three facts, the
// two the fold needs, `attempt_token` and `checkpoint_seq`, and the row's
// `appended_at`, which the page's clocks read (#1374). The envelope half is imported
// from the generated types and never restated; when the stream route joins the
// document, this file goes and the generator takes over.

import type { Event } from "./envelope.generated";

/** A row as `read_log` returns it and as every frame's `data` carries it. */
export interface LogRow extends Event {
  /** The attempt that wrote the row; null on a platform row. */
  attempt_token: string | null;
  /** The checkpoint the row's batch confirmed; null when it confirmed none. */
  checkpoint_seq: number | null;
  /** When the row was stored (ISO 8601), the column the record's clocks read (#1374);
   *  null on a pushed row, which is never stored. */
  appended_at: string | null;
}

/** A durable row: stored, `id = seq` on its frame, replayed on `Last-Event-ID`. */
export interface DurableRow extends LogRow {
  seq: number;
  ephemeral: false;
}

/** A pushed row: `stream` (`context` is a stored row since #399), no `id`, never replayed. */
export interface EphemeralRow extends LogRow {
  ephemeral: true;
}

/** The `event: heartbeat` frame's data: the stream's current seq, a resume point, not a row. */
export interface Heartbeat {
  seq: number;
}

/** The stream's path, beside the runtime's `POST /runs/{run_id}/events` (`routes/stream.py`). */
export function streamPath(runId: string): string {
  return `/runs/${encodeURIComponent(runId)}/events/stream`;
}

/** The heartbeat frame's `event:` name (`sse.py`, `HEARTBEAT_EVENT`). */
export const HEARTBEAT_EVENT = "heartbeat";
